import React, {
  ComponentType,
  useEffect,
  useMemo,
  useRef,
  useState,
  useCallback
} from 'react'
import { ThemeProvider, useTheme } from 'styled-components'
import { WindowSize, ContainerSize } from '../../../types'

const { MOBILE_MAX, TABLET_PORTRAIT_MAX, TABLET_LANDSCAPE_MAX } = WindowSize
const { MOBILE, TABLET_PORTRAIT, TABLET_LANDSCAPE, DESKTOP } = ContainerSize

export function withContainerSize<P>(WrappedComponent: ComponentType<P>) {
  return function ContainerSizeProvider(props: P) {
    const defaultContainerSize =
      window.innerWidth <= MOBILE_MAX ? MOBILE : DESKTOP
    const [containerSize, setContainerSize] = useState(defaultContainerSize)
    const containerRef = useRef<HTMLElement>()
    const resizeTimeoutRef = useRef<number>()
    const parentTheme = useTheme()
    const theme = useMemo(() => {
      const layoutSize = parentTheme?.layoutSize || containerSize
      return { ...parentTheme, containerSize, layoutSize }
    }, [parentTheme, containerSize])

    const updateContainerSize = useCallback((clientWidth: number) => {
      if (clientWidth <= MOBILE_MAX) {
        setContainerSize(MOBILE)
      } else if (clientWidth <= TABLET_PORTRAIT_MAX) {
        setContainerSize(TABLET_PORTRAIT)
      } else if (clientWidth <= TABLET_LANDSCAPE_MAX) {
        setContainerSize(TABLET_LANDSCAPE)
      } else {
        setContainerSize(DESKTOP)
      }
    }, [])

    useEffect(() => {
      const target = containerRef.current
      if (target) {
        const initialWidth = target.getBoundingClientRect().width
        updateContainerSize(initialWidth)

        const resizeObserver = new ResizeObserver((entries) => {
          if (resizeTimeoutRef.current) {
            window.clearTimeout(resizeTimeoutRef.current)
          }

          resizeTimeoutRef.current = window.setTimeout(() => {
            entries.forEach(({ target }) => {
              if (target instanceof HTMLElement && target.isConnected) {
                const clientWidth = target.getBoundingClientRect().width
                updateContainerSize(clientWidth)
              }
            })
          }, 100)
        })
        resizeObserver.observe(target)
        return () => {
          if (resizeTimeoutRef.current) {
            window.clearTimeout(resizeTimeoutRef.current)
          }
          resizeObserver.unobserve(target)
        }
      }
    }, [updateContainerSize])

    return (
      <ThemeProvider theme={theme}>
        <WrappedComponent ref={containerRef} {...props} />
      </ThemeProvider>
    )
  }
}
